import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { io } from "socket.io-client";
import { useNavigate } from "react-router-dom";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { toast } from "react-toastify";
import {
  FiBox,
  FiCreditCard,
  FiPlus,
} from "react-icons/fi";
import { RiWallet3Line } from "react-icons/ri";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import API_URL from "../config/config";
import OrderCard from "../components/OrderCard";
import OrderDetails from "../components/OrderDetails";
import { getCachedPageData, setCachedPageData } from "../utils/pageDataCache";

const TAB_META = [
  { key: "cod", label: "COD", icon: FiBox },
  { key: "online", label: "Online", icon: FiCreditCard },
  { key: "wallet", label: "Wallet", icon: RiWallet3Line },
];

const normalizePayment = (value) => String(value || "").toLowerCase();
const ORDER_PAGE_SIZE = 50;
const getOrdersCacheKey = (token) => `orders:${token || "anonymous"}`;

const OrderHistory = () => {
  const cachedOrders = getCachedPageData(
    getOrdersCacheKey(localStorage.getItem("token"))
  );
  const [orders, setOrders] = useState(() => cachedOrders?.orders || []);
  const [loading, setLoading] = useState(() => !cachedOrders);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState(
    () => cachedOrders?.pagination || { page: 1, total: 0, hasMore: false }
  );
  const [selectedOrder, setSelectedOrder] = useState(null);
  const navigate = useNavigate();

  const fetchOrders = useCallback(async ({ page = 1, append = false, force = false } = {}) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No token found. Please sign in.");
      setLoading(false);
      navigate("/");
      return;
    }

    const cacheKey = getOrdersCacheKey(token);
    const cachedSnapshot = !append && !force
      ? getCachedPageData(cacheKey)
      : null;

    if (cachedSnapshot) {
      setOrders(cachedSnapshot.orders);
      setPagination(cachedSnapshot.pagination);
      setLoading(false);
      return;
    }

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await axios.get(`${API_URL}/order/customer/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          compact: true,
          page,
          limit: ORDER_PAGE_SIZE,
        },
      });
      const data = response.data;
      if (data.error) {
        toast.error(data.message || "Failed to fetch orders");
        if (!append) {
          setOrders([]);
        }
      } else {
        const nextOrders = data.order || data.orders || [];
        const nextPagination = data.pagination || {
          page,
          total: nextOrders.length,
          hasMore: false,
        };

        setOrders((currentOrders) => {
          if (!append) {
            setCachedPageData(cacheKey, {
              orders: nextOrders,
              pagination: nextPagination,
            });
            return nextOrders;
          }

          const knownOrderIds = new Set(
            currentOrders.map((order) => String(order?._id || order?.orderId || ""))
          );
          const mergedOrders = [
            ...currentOrders,
            ...nextOrders.filter(
              (order) => !knownOrderIds.has(String(order?._id || order?.orderId || ""))
            ),
          ];
          setCachedPageData(cacheKey, {
            orders: mergedOrders,
            pagination: nextPagination,
          });
          return mergedOrders;
        });
        setPagination(nextPagination);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.clear();
        toast.error("Session expired. Please sign in again.");
        navigate("/");
      } else {
        toast.error(error.response?.data?.message || "Error fetching orders");
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const loadMoreOrders = () => {
    if (loadingMore || !pagination.hasMore) {
      return;
    }

    fetchOrders({ page: Number(pagination.page || 1) + 1, append: true });
  };

  const orderRoomIds = useMemo(
    () =>
      orders
        .flatMap((order) => [String(order?._id || ""), String(order?.orderId || "")])
        .filter(Boolean),
    [orders]
  );
  const orderRoomIdsKey = useMemo(() => orderRoomIds.join("|"), [orderRoomIds]);

  useEffect(() => {
    if (!orderRoomIds.length) return undefined;

    const socket = io(API_URL.current, {
      transports: ["websocket", "polling"],
    });

    const joinOrderRooms = () => {
      orderRoomIds.forEach((orderId) => {
        socket.emit("track_order", { orderId });
      });
    };

    const handleOrderStatusUpdate = (payload = {}) => {
      const payloadOrderId = String(payload.orderId || "");
      const nextStatus = String(payload.currentStatus || "").trim();
      if (!payloadOrderId || !nextStatus) return;

      setOrders((currentOrders) =>
        currentOrders.map((currentOrder) => {
          const matchesOrder =
            String(currentOrder?._id || "") === payloadOrderId ||
            String(currentOrder?.orderId || "") === payloadOrderId;

          if (!matchesOrder) return currentOrder;
          if (String(currentOrder?.status || "") === nextStatus) return currentOrder;

          return {
            ...currentOrder,
            status: nextStatus,
            updatedAt: payload.updatedAt || new Date().toISOString(),
          };
        })
      );

      setSelectedOrder((currentSelectedOrder) => {
        if (!currentSelectedOrder) return currentSelectedOrder;

        const matchesSelectedOrder =
          String(currentSelectedOrder?._id || "") === payloadOrderId ||
          String(currentSelectedOrder?.orderId || "") === payloadOrderId;

        if (!matchesSelectedOrder) return currentSelectedOrder;
        if (String(currentSelectedOrder?.status || "") === nextStatus) {
          return currentSelectedOrder;
        }

        return {
          ...currentSelectedOrder,
          status: nextStatus,
          updatedAt: payload.updatedAt || new Date().toISOString(),
        };
      });
    };

    socket.on("connect", joinOrderRooms);
    socket.on("order_status_update", handleOrderStatusUpdate);

    if (socket.connected) {
      joinOrderRooms();
    }

    return () => {
      socket.off("connect", joinOrderRooms);
      socket.off("order_status_update", handleOrderStatusUpdate);
      socket.disconnect();
    };
  }, [orderRoomIdsKey]);

  const ordersByType = useMemo(() => {
    return {
      cod: orders.filter((order) => normalizePayment(order?.payment_method).includes("cod")),
      online: orders.filter((order) => {
        const value = normalizePayment(order?.payment_method);
        return value.includes("online") || value.includes("upi") || value.includes("card");
      }),
      wallet: orders.filter((order) => normalizePayment(order?.payment_method).includes("wallet")),
    };
  }, [orders]);

  const renderEmptyState = (label) => (
    <div className="flex min-h-[220px] flex-col items-center justify-center rounded-[20px] border border-dashed border-[#eadfcb] bg-[#fffdfa] px-4 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#fff4dd] text-[#f5a10a]">
        <FiBox className="text-[28px]" />
      </div>
      <p className="mt-4 text-[20px] font-semibold text-[#1f2432] sm:text-[22px]">
        No {label} orders yet
      </p>
      <p className="mt-2 max-w-[360px] text-[14px] leading-6 text-[#697386] sm:text-[15px]">
        Track and manage your delivery activity here once your orders are placed.
      </p>
    </div>
  );

  const renderOrderList = (list, label) => {
    if (loading) {
      return (
        <div className="flex min-h-[240px] items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-[4px] border-[#f4c75b] border-t-[#f59e0b]" />
        </div>
      );
    }

    if (!list.length) {
      return renderEmptyState(label);
    }

    return (
      <div className="max-h-[500px] space-y-3 overflow-y-auto pr-2">
        {list.map((order) => (
          <OrderCard
            key={order._id || order.orderId}
            order={order}
            onClick={setSelectedOrder}
          />
        ))}
      </div>
    );
  };

  return (
    <section className="min-h-screen w-screen overflow-x-hidden bg-[linear-gradient(180deg,#fdf8ee_0%,#fffdf8_22%,#fff9ef_100%)] text-[#1d2433]">
      <div className="flex min-h-screen w-full flex-col px-0 pb-3 pt-2 sm:pt-3">
        <Navbar />

        <div className="mt-2 w-full overflow-hidden rounded-[24px] bg-[linear-gradient(135deg,#fffefb_0%,#fffdf8_34%,#fff7ea_70%,#fffef9_100%)] shadow-[0_18px_50px_rgba(177,146,90,0.12)]">
          <div className="px-3 pb-4 pt-3 sm:px-5 lg:px-8">
            <div className="rounded-[20px] border border-[#eee3d1] bg-white/92 px-3 py-3 shadow-[0_14px_36px_rgba(191,166,116,0.10)] sm:px-5 sm:py-5 lg:rounded-[24px] lg:px-6 lg:py-6">
              <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-center md:gap-5">
                <div>
                  <h1 className="text-[24px] font-extrabold tracking-[-0.04em] text-[#1f2432] sm:text-[34px] lg:text-[40px]">
                    Your Orders
                  </h1>
                  <p className="mt-1.5 text-[13px] text-[#7a8497] sm:text-[15px] lg:text-[16px]">
                    Track and manage all your delivery orders
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/create-order")}
                  className="inline-flex h-[42px] items-center justify-center gap-2 rounded-[12px] bg-[linear-gradient(180deg,#f9a40d_0%,#f39700_100%)] px-4 text-[14px] font-semibold text-white shadow-[0_12px_24px_rgba(243,151,0,0.22)] transition hover:brightness-105 sm:h-[48px] sm:px-6 sm:text-[16px] lg:h-[52px] lg:px-6 lg:text-[17px]"
                >
                  <FiPlus className="text-[18px]" />
                  Create Order
                </button>
              </div>

              <div className="mt-5">
                <Tabs>
                  <TabList className="grid grid-cols-3 gap-2 rounded-[18px] bg-[#fff7eb] p-2 sm:gap-2.5 sm:p-2.5">
                    {TAB_META.map(({ key, label, icon: Icon }) => (
                      <Tab
                        key={key}
                        selectedClassName="!border-[#f5aa1b] !bg-white !text-[#1f2432] !shadow-[0_12px_30px_rgba(245,170,27,0.18)]"
                        className="flex min-h-[58px] cursor-pointer flex-col items-center justify-center gap-1 rounded-[14px] border border-transparent px-2 py-2.5 text-center text-[12px] font-semibold text-[#596273] outline-none transition sm:min-h-[64px] sm:flex-row sm:gap-2.5 sm:px-3 sm:py-3 sm:text-[14px] lg:text-[15px]"
                      >
                        <Icon className="text-[16px] text-[#8b94a7] sm:text-[18px]" />
                        <span>{label}</span>
                        <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#f4f5f8] px-1.5 text-[10px] font-semibold text-[#6b7280] sm:h-6 sm:min-w-6 sm:px-2 sm:text-[11px]">
                          {ordersByType[key].length}
                        </span>
                      </Tab>
                    ))}
                  </TabList>

                  <div className="mt-3 rounded-[18px] border border-[#ebe3d6] bg-[#fffefb] px-2 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] sm:px-3 sm:py-3 lg:mt-4 lg:rounded-[22px] lg:px-4 lg:py-4">
                    <TabPanel>{renderOrderList(ordersByType.cod, "COD")}</TabPanel>
                    <TabPanel>{renderOrderList(ordersByType.online, "online")}</TabPanel>
                    <TabPanel>{renderOrderList(ordersByType.wallet, "wallet")}</TabPanel>

                    {!loading && pagination.hasMore ? (
                      <div className="flex justify-center pt-4">
                        <button
                          type="button"
                          onClick={loadMoreOrders}
                          disabled={loadingMore}
                          className="inline-flex min-h-[42px] items-center justify-center rounded-xl border border-[#f0dcae] bg-[#fff9eb] px-5 text-sm font-semibold text-[#7c5b16] transition hover:bg-[#fff3d2] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {loadingMore ? "Loading orders..." : "Load more orders"}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </Tabs>
              </div>
            </div>
          </div>

          <Footer className="pt-0" />
        </div>
      </div>

      {selectedOrder && (
        <OrderDetails
          order={selectedOrder}
          onOrderUpdated={() => fetchOrders({ force: true })}
          onClose={() => setSelectedOrder(null)}
        />
      )}
    </section>
  );
};

export default OrderHistory;
